import { Injectable, signal, type Signal, type WritableSignal } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

/** A task the panel reported as created, so the requester can react to its id. */
export interface PlatformTaskCreatedEvent {
  taskUuid: string;
  /** The entity the task was created under (`null` when the form didn't carry one). */
  entityId: string | null;
}

/** Prefill values for a requested create-task form (subset of the panel's NewTaskDefaults). */
export interface PlatformTaskCreateDefaults {
  title?: string;
}

export interface PlatformTaskInterfaceContext {
  entityType: string | null;
  pageId: string | null;
  entityName: string | null;
  projectId?: string | null;
}

const DEFAULT_CONTEXT: PlatformTaskInterfaceContext = {
  entityType: null,
  pageId: null,
  entityName: null,
  projectId: null,
};

@Injectable({ providedIn: 'root' })
export class PlatformTaskInterfaceStateService {
  private readonly subject: BehaviorSubject<PlatformTaskInterfaceContext> =
    new BehaviorSubject<PlatformTaskInterfaceContext>({ ...DEFAULT_CONTEXT });

  readonly context$: Observable<PlatformTaskInterfaceContext> = this.subject.asObservable();

  /** Consume-once request to open the create-task form once the panel is ready (e.g. drive calendar click). */
  private readonly _pendingCreateTask: WritableSignal<boolean> = signal(false);
  readonly pendingCreateTask: Signal<boolean> = this._pendingCreateTask.asReadonly();

  /** Prefill values accompanying the pending create request; lives and dies with it. */
  private readonly _pendingCreateTaskDefaults: WritableSignal<PlatformTaskCreateDefaults | null> =
    signal<PlatformTaskCreateDefaults | null>(null);
  readonly pendingCreateTaskDefaults: Signal<PlatformTaskCreateDefaults | null> =
    this._pendingCreateTaskDefaults.asReadonly();

  /** Consume-once request to open a task's detail view once the panel is ready (e.g. drive default task). */
  private readonly _pendingOpenTask: WritableSignal<string | null> = signal<string | null>(null);
  readonly pendingOpenTask: Signal<string | null> = this._pendingOpenTask.asReadonly();

  /** Fired by the panel wrapper when a task is created with a known uuid. */
  readonly taskCreated$: Subject<PlatformTaskCreatedEvent> = new Subject<PlatformTaskCreatedEvent>();

  /** Fired when a requested open-task turned out to no longer exist (definite 404). */
  readonly openTaskMissing$: Subject<string> = new Subject<string>();

  private resetTimeoutId: number | null = null;

  public requestCreateTask(defaults?: PlatformTaskCreateDefaults): void {
    this._pendingCreateTaskDefaults.set(defaults ?? null);
    this._pendingCreateTask.set(true);
  }

  public clearCreateTaskRequest(): void {
    this._pendingCreateTask.set(false);
    this._pendingCreateTaskDefaults.set(null);
  }

  public requestOpenTask(taskUuid: string): void {
    this._pendingOpenTask.set(taskUuid);
  }

  public clearOpenTaskRequest(): void {
    this._pendingOpenTask.set(null);
  }

  public notifyTaskCreated(event: PlatformTaskCreatedEvent): void {
    this.taskCreated$.next(event);
  }

  public notifyOpenTaskMissing(taskUuid: string): void {
    this.openTaskMissing$.next(taskUuid);
  }

  public getCurrentValue(): PlatformTaskInterfaceContext {
    return this.subject.getValue();
  }

  public setContext(update: Partial<PlatformTaskInterfaceContext> | null): void {
    this.clearPendingReset();
    this.clearCreateTaskRequest();
    this.clearOpenTaskRequest();
    if (!update) {
      this.resetContext();
      return;
    }
    const current: PlatformTaskInterfaceContext = this.subject.getValue();
    this.subject.next({
      entityType: update.entityType ?? current.entityType,
      pageId: update.pageId ?? current.pageId,
      entityName: update.entityName ?? current.entityName,
      projectId: update.projectId ?? current.projectId,
    });
  }

  public resetContext(): void {
    this.clearCreateTaskRequest();
    this.clearOpenTaskRequest();
    this.subject.next({ ...DEFAULT_CONTEXT });
  }

  public scheduleReset(delayMs: number = 300): void {
    this.clearPendingReset();
    this.resetTimeoutId = setTimeout(() => {
      this.resetTimeoutId = null;
      this.resetContext();
    }, delayMs) as unknown as number;
  }

  private clearPendingReset(): void {
    if (this.resetTimeoutId != null) {
      clearTimeout(this.resetTimeoutId);
      this.resetTimeoutId = null;
    }
  }
}
